import { PayloadAction } from '@reduxjs/toolkit'
import { isSpotSubType } from '@spiaggeit/spit-core'
import {
  FeaturedService,
  FormattedServices,
  ServiceLimitType,
  TimeSchedule,
} from '@/models/service'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { getReservationSeatsAmount } from '@/utils/getReservationSeatsAmount'
import {
  getBoughtServicesTotal,
  getTimeScheduleKey,
} from '@/utils/timeSchedules'
import { CartSliceState } from '../type'

function buildBoughtServiceEntry({
  days,
  featuredService,
  quantity,
  serviceId,
  timeSchedule,
}: {
  days: number[]
  featuredService: FeaturedService
  quantity: number
  serviceId: number
  timeSchedule: TimeSchedule
}) {
  return {
    [getTimeScheduleKey(timeSchedule)]: {
      bought: quantity,
      days,
      serviceId,
      serviceName: featuredService.name,
      timeSchedule,
    },
  }
}

export function addFeaturedServiceToCart(
  state: CartSliceState,
  action: PayloadAction<{
    days: number[]
    featuredService: FeaturedService
    formattedServices: FormattedServices
    quantity: number
    timeSchedule: TimeSchedule
  }>
) {
  const { payload } = action
  const key = payload.featuredService.serviceId

  if (state.activeBookingType === BookingAvailability.TICKETS) {
    state.cartReservations = [
      {
        ...state.cartReservations[0],
        boughtServices: {
          ...state.cartReservations[0].boughtServices,
          [key]: buildBoughtServiceEntry({
            days: payload.days,
            featuredService: payload.featuredService,
            quantity: payload.quantity,
            serviceId: key,
            timeSchedule: payload.timeSchedule,
          }),
        },
      },
    ]
    return
  }

  const newCartReservations = state.cartReservations.map((res) => {
    if (!res.boughtServices[key]) {
      return res
    }

    const newBoughtServices = { ...res.boughtServices }
    delete newBoughtServices[key]
    return {
      ...res,
      boughtServices: newBoughtServices,
    }
  })

  const spotsReservations = newCartReservations.filter(
    (res) =>
      isSpotSubType(res.spotType) &&
      payload.featuredService.sectors.includes(res.sectorId)
  )

  if (spotsReservations.length === 0 || payload.quantity <= 0) {
    state.cartReservations = newCartReservations
    return
  }

  const getSpotCapacity = (res: (typeof spotsReservations)[number]) => {
    if (
      payload.featuredService.minimumLimitType ===
      ServiceLimitType.NUMBER_OF_PIECES
    ) {
      return getReservationSeatsAmount(res)
    }

    if (
      payload.featuredService.minimumLimitType ===
      ServiceLimitType.NUMBER_OF_DAYS
    ) {
      return payload.days.length
    }

    return payload.featuredService.minimumQuantity
  }

  for (let i = 0; i < payload.quantity; i++) {
    const res = spotsReservations.find((spotReservation) => {
      const quantity = getBoughtServicesTotal(
        spotReservation.boughtServices[key]
      )
      return quantity < getSpotCapacity(spotReservation)
    })

    const toUpdate = res ?? spotsReservations[0]
    const newQuantity = getBoughtServicesTotal(toUpdate.boughtServices[key])
    toUpdate.boughtServices[key] = buildBoughtServiceEntry({
      days: payload.days,
      featuredService: payload.featuredService,
      quantity: newQuantity + 1,
      serviceId: key,
      timeSchedule: payload.timeSchedule,
    })
  }

  state.cartReservations = newCartReservations
}
